"use client";

import {autoTableColumnLabels} from "@/features/i18n/autoTableColumns";
import {Input} from "@/shared/components/ui/input";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {AutoTableInterface} from "@/features/types/AutoTableInterface";
import {formatDetailsTableCellValue, getAutoDetailsDisplayValue, getProcessNumberDisplayValue} from "../autoDetailsUtils";
import {ReactTable, ReactTableCell, ReactTableState} from "@/shared/components/ReactTable";
import {TableRow} from "@/shared/components/ui/table";
import {paymDocColumns, PaymDocTableInterface,} from "@/features/types/PaymDocTableInterface";
import {createColumnHelper, getCoreRowModel, useReactTable} from "@tanstack/react-table";
import {getPaymDocTableData} from "@/features/controllers/PaymDocTableController";

export interface AutoDetailsSellingTabProps {
    data: AutoTableInterface;
}

const sellingFieldKeys: Array<keyof AutoTableInterface> = [
    "f_sales_cli_o_name",
    "f_sales_dat",
    "f_sales_user_name",
    "f_supp_2_status_desc",
    "f_sales_agr_issue_dat",
    "f_sales_invc_internal_d",
    "f_sales_invc_num",
    "f_sales_book_mnt_D",
    "f_sales_book_mnt_C",
];

const paymDocColumnHelper = createColumnHelper<PaymDocTableInterface>();

export const AutoDetailsSellingTab = ({data}: AutoDetailsSellingTabProps) => {
    const [paymRows, setPaymRows] = useState<PaymDocTableInterface[]>([]);
    const [paymState, setPaymState] = useState<ReactTableState>("empty");
    const paymOuterContainerRef = useRef<HTMLDivElement>(null);
    const paymTableContainerRef = useRef<HTMLDivElement>(null);

    const paymTableColumns = useMemo(
        () =>
            paymDocColumns.map((col) =>
                paymDocColumnHelper.accessor(col.accessorKey, {
                    header: col.header,
                    size: col.size,
                    cell: (info) => formatDetailsTableCellValue(info.getValue()),
                })
            ),
        []
    );

    const paymTable = useReactTable<PaymDocTableInterface>({
        data: paymRows,
        columns: paymTableColumns,
        columnResizeMode: "onChange",
        getCoreRowModel: getCoreRowModel(),
    });

    useEffect(() => {
        if (!data.f_ord_num || !data.f_ord_d) return;

        let cancelled = false;
        setPaymState("loading");

        getPaymDocTableData(String(data.f_ord_num), String(data.f_ord_d))
            .then((rows) => {
                if (!cancelled) {
                    const withSalesUser = rows.map((r) => ({
                        ...r,
                        f_sales_user_name: data.f_sales_user_name ?? "",
                    }));
                    setPaymRows(withSalesUser);
                    setPaymState(withSalesUser.length === 0 ? "empty" : "ready");
                }
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                console.error("Blad pobierania danych PaymDoc:", err);
                setPaymRows([]);
                setPaymState("error");
            });

        return () => {
            cancelled = true;
        };
    }, [data.f_ord_num, data.f_ord_d, data.f_sales_user_name]);

    const processNumberRow = paymRows[0] ?? null;

    return (
        <div className="flex flex-col gap-6">
            <div className="grid grid-cols-2 gap-x-3 gap-y-2 min-w-0 md:grid-cols-3 xl:grid-cols-4">
                <div className="grid w-full min-w-0 grid-cols-[100px_minmax(0,1fr)] items-center gap-2 text-left">
                    <span className="text-xs text-muted-foreground truncate">
                        Numer procesu
                    </span>
                    <Input
                        readOnly
                        value={getProcessNumberDisplayValue(processNumberRow)}
                        className="h-7 text-sm font-semibold w-full min-w-0 bg-muted/10"
                    />
                </div>
                {sellingFieldKeys.map((key) => (
                    <div
                        key={key}
                        className="grid w-full min-w-0 grid-cols-[100px_minmax(0,1fr)] items-center gap-2 text-left"
                    >
                        <span className="text-xs text-muted-foreground truncate">
                            {autoTableColumnLabels[key] ?? key}
                        </span>
                        <Input
                            readOnly
                            value={getAutoDetailsDisplayValue(data, key)}
                            className="h-7 text-sm w-full min-w-0 bg-muted/10"
                        />
                    </div>
                ))}
            </div>

            <div className="space-y-2">
                <ReactTable
                    table={paymTable}
                    state={paymState}
                    stateMessages={{ emptyMessage: "Brak danych płatności dla wybranego zamówienia." }}
                    heightClassName="h-[36vh]"
                    outerContainerRef={(node) => {
                        paymOuterContainerRef.current = node;
                    }}
                    tableContainerRef={(node) => {
                        paymTableContainerRef.current = node;
                    }}
                    renderItem={(row, virtualItem) => (
                        <TableRow
                            key={row.id}
                            style={{
                                height: `${virtualItem.size}px`,
                                transform: `translateY(${virtualItem.start}px)`,
                            }}
                        >
                            {row.getVisibleCells().map((cell) => (
                                <ReactTableCell key={cell.id} cell={cell}/>
                            ))}
                        </TableRow>
                    )}
                />
            </div>
        </div>
    );
};
